/**
 * LeadTriageDashboard — Phase 4 (2026-05-26).
 *
 * Miss Deepika's only-screen workspace. Three tabs (Unassigned / Assigned /
 * Self-made), 9 columns matching Manak's screenshot + Application Type +
 * B2B/B2C. Per-cell "unverified" rendering as dashed-border empty cell with
 * tooltip. Inline assign-to-team dropdown with optimistic toast feedback.
 *
 * Talks to:
 *   GET  /api/v1/leads/triage-dashboard?tab=...&search=...&sort=...&limit=...
 *   POST /api/v1/leads/:id/assign-team   { teamId }
 *   GET  /api/v1/leads/triage-teams
 *   PATCH /api/v1/auth/me-update         { firstName, avatar, currentPassword, newPassword }
 *
 * Pure presentational with `fetch` — no extra deps. Tailwind classes only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import { TierChip } from './TierChip';
import { LeadResponsesDrawer } from './LeadResponsesDrawer';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DashboardRow {
  id: string;
  createdAt: string;
  enquiryDate: string;
  source: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  location: string | null;
  requirement: string | null;
  applicationType: string | null;
  businessSegment: string | null;
  verifiedStatus: 'verified' | 'unverified';
  verifiedBy: 'chatkart' | 'manual' | 'avika' | null;
  verifiedByName: string | null;
  assignedToName: string | null;
  coordinatorName?: string | null;
  teamName: string | null;
  // ── Avika (2026-06-10) enrichment ──────────────────────────────────────
  score: number | null;
  tier: 'HOT' | 'WARM' | 'COLD' | null;
  scoringModel: 'end_customer' | 'no_price' | 'b2b' | null;
  capacity: string | null;
  capacitySource: string | null;
  quantity: string | null;
  timeline: string | null;
  priceShared: boolean;
  priceRange: string | null;
  state: string | null;
  waLineLabel: 'line1' | 'line2' | null;
  isB2bPriority: boolean;
  isOutOfBudget: boolean;
}

interface VerifyForm {
  productCategory: string;
  applicationType: '' | 'Residential' | 'Commercial' | 'B2B';
  businessSegment: '' | 'B2B' | 'B2C';
  location: string;
  requirement: string;
  notes: string;
}
const BLANK_VERIFY_FORM: VerifyForm = {
  productCategory: '',
  applicationType: '',
  businessSegment: '',
  location: '',
  requirement: '',
  notes: '',
};

interface DashboardResponse {
  success: true;
  data: DashboardRow[];
  total: number;
  tab: string;
  limit: number;
  offset: number;
}

interface Team {
  id: string;
  name: string;
}

interface RosterPerson { id: string; name: string }
interface TeamMembers { teamId: string; teamName: string; manager: RosterPerson | null; coordinator: RosterPerson | null; executives: RosterPerson[] }

type Tab = 'unassigned' | 'assigned' | 'self_made';
const TABS: { key: Tab; label: string }[] = [
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'self_made', label: 'Self-made' },
];

// ─── Per-cell unverified rendering ───────────────────────────────────────────

function Cell({ value, dim = false }: { value: string | null | undefined; dim?: boolean }) {
  if (!value || value.trim().length === 0) {
    return (
      <span
        className="inline-flex items-center text-xs italic text-gray-400 border border-dashed border-gray-300 rounded px-1.5 py-0.5"
        title="Not yet verified by Ananya"
      >
        unverified
      </span>
    );
  }
  return <span className={dim ? 'text-gray-500' : 'text-gray-900'}>{value}</span>;
}

// "3 nos × 2000 L" when a quantity > 1 is captured, else just the capacity.
// Quantity is NEVER folded into capacity (handover #16) — they are distinct.
export function formatCapacitySummary(capacity: string | null, quantity?: string | number | null): string {
  const q = Number(quantity);
  const cap = String(capacity || '').trim();
  if (!cap) return '';
  if (!Number.isFinite(q) || q <= 1) return cap;
  return `${q} nos × ${cap}`;
}

// Build a clean, copy-ready text summary of a lead (everything: name → number →
// requirement → score). Read-only — used by the Copy column.
function buildLeadSummary(r: DashboardRow): string {
  const L: string[] = [];
  L.push(`Lead: ${r.name || 'Unknown'}`);
  if (r.phone) L.push(`Phone: ${r.phone}`);
  if (r.email) L.push(`Email: ${r.email}`);
  L.push(`Enquiry date: ${formatDate(r.createdAt)}`);
  const line = r.waLineLabel === 'line1' ? 'Line 1' : r.waLineLabel === 'line2' ? 'Line 2' : null;
  L.push(`Source: ${r.source || 'Unknown'}${line ? ` (${line})` : ''}`);
  const loc = [r.location, r.state].filter(Boolean).join(', ');
  if (loc) L.push(`Location: ${loc}`);
  if (r.requirement) L.push(`Requirement: ${r.requirement}`);
  if (r.applicationType) L.push(`Application: ${r.applicationType}${r.businessSegment ? ` (${r.businessSegment})` : ''}`);
  if (r.capacity) L.push(`Capacity: ${formatCapacitySummary(r.capacity, r.quantity)}`);
  if (r.timeline) L.push(`Timeline: ${r.timeline}`);
  if (r.priceShared) L.push(`Indicative price shared: ${r.priceRange || 'Yes'}`);
  if (r.score != null) {
    const flags = [r.isB2bPriority ? 'B2B PRIORITY' : '', r.isOutOfBudget ? 'OUT OF BUDGET' : ''].filter(Boolean).join(', ');
    L.push(`Lead score: ${r.score}${r.tier ? ` (${r.tier})` : ''}${flags ? ` — ${flags}` : ''}`);
  }
  if (r.scoringModel) L.push(`Scoring model: ${r.scoringModel}`);
  const verified = r.verifiedStatus === 'verified'
    ? `Verified${r.verifiedBy ? ` (${r.verifiedBy}${r.verifiedByName ? ': ' + r.verifiedByName : ''})` : ''}`
    : 'Unverified';
  L.push(`Status: ${verified}`);
  if (r.teamName || r.assignedToName) L.push(`Assigned: ${r.teamName || r.assignedToName}`);
  return L.join('\n');
}

// One-click copy-to-clipboard button (read-only). Shows a brief "Copied" state.
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`text-xs font-medium rounded px-2 py-0.5 border transition-colors ${copied ? 'text-green-700 border-green-400 bg-green-50' : 'text-gray-700 border-gray-300 hover:bg-gray-100'}`}
      title="Copy the full lead summary to clipboard"
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

function VerifiedBadge({ status }: { status: 'verified' | 'unverified' }) {
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> verified
      </span>
    );
  }
  return null;
}

// ─── Date formatter (DD-MMM matching screenshot "01-May") ───────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}`;
}

// ─── Toast ───────────────────────────────────────────────────────────────────

interface Toast {
  id: string;
  text: string;
  undo?: () => void;
}

// ─── Filters + sort ──────────────────────────────────────────────────────────

type SortKey =
  | 'newest' | 'oldest'
  | 'name_asc' | 'name_desc'
  | 'source_asc' | 'source_desc'
  | 'location_asc' | 'location_desc'
  // Avika (2026-06-10) — lead-score sorts.
  | 'score_desc' | 'score_asc';

type VerifiedFilter = 'any' | 'verified' | 'unverified';
type VerifiedSourceFilter = 'any' | 'chatkart' | 'manual';

// Avika (2026-06-16) — lead-tier filter. 'BLANK' = unscored leads (interestLevel null, rendered "—").
type TierFilter = 'any' | 'HOT' | 'WARM' | 'COLD' | 'BLANK';

// Avika (2026-06-10) — WhatsApp line filter.
type LineFilter = 'all' | 'line1' | 'line2';

interface FilterState {
  dateFrom: string;
  dateTo: string;
  sources: string[];
  applicationTypes: string[];
  businessSegments: string[];
  verified: VerifiedFilter;
  verifiedSource: VerifiedSourceFilter;
  tier: TierFilter;
  location: string;
}
const BLANK_FILTERS: FilterState = {
  dateFrom: '', dateTo: '',
  sources: [], applicationTypes: [], businessSegments: [],
  verified: 'any', verifiedSource: 'any', tier: 'any',
  location: '',
};

const SOURCE_OPTIONS = ['WEBSITE', 'INDIAMART', 'AI CHATBOX'];
const APP_TYPE_OPTIONS = ['Residential', 'Commercial', 'B2B'];
const SEGMENT_OPTIONS = ['B2B', 'B2C'];

// Date range presets — returns [from, to] in YYYY-MM-DD
function presetRange(preset: string): [string, string] {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const to = fmt(today);
  if (preset === 'today') return [to, to];
  if (preset === '7d')  { const d = new Date(today); d.setDate(d.getDate() - 7);  return [fmt(d), to]; }
  if (preset === '30d') { const d = new Date(today); d.setDate(d.getDate() - 30); return [fmt(d), to]; }
  if (preset === 'mtd') { const d = new Date(today.getFullYear(), today.getMonth(), 1); return [fmt(d), to]; }
  return ['', ''];
}

// ─── Filter → querystring builder (shared by data-load + XLSX export) ────────

// Serialises the active filter state into query params shared by the data-load
// effect AND the XLSX export. Does NOT include limit/offset — the caller adds
// pagination when it needs it (the export does not).
function buildTriageFilterParams(opts: {
  tab: Tab; search: string; sortKey: SortKey; filters: FilterState; lineFilter: LineFilter;
}): URLSearchParams {
  const { tab, search, sortKey, filters, lineFilter } = opts;
  const q = new URLSearchParams({ tab, sort: sortKey });
  if (search.trim().length >= 2) q.set('search', search.trim());
  if (filters.dateFrom)                 q.set('dateFrom', filters.dateFrom);
  if (filters.dateTo)                   q.set('dateTo', filters.dateTo);
  if (filters.sources.length)           q.set('source', filters.sources.join(','));
  if (filters.applicationTypes.length)  q.set('applicationType', filters.applicationTypes.join(','));
  if (filters.businessSegments.length)  q.set('businessSegment', filters.businessSegments.join(','));
  if (filters.location.trim().length >= 2) q.set('location', filters.location.trim());
  if (filters.verified !== 'any')       q.set('verified', filters.verified);
  if (filters.verifiedSource !== 'any') q.set('verifiedSource', filters.verifiedSource);
  if (filters.tier !== 'any')           q.set('tier', filters.tier);
  if (lineFilter !== 'all')             q.set('line', lineFilter);
  return q;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LeadTriageDashboard() {
  // Drag-to-pan state for the wide table (Windows mouse users — see the
  // Table comment below). Refs, not state: panning must not re-render.
  const hscrollRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ startX: number; startLeft: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const [tab, setTab] = useState<Tab>('unassigned');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [verifyOpenFor, setVerifyOpenFor] = useState<DashboardRow | null>(null);
  const [verifyForm, setVerifyForm] = useState<VerifyForm>(BLANK_VERIFY_FORM);
  const [verifySubmitting, setVerifySubmitting] = useState(false);
  // Assign modal (team → executive | manager+coordinator)
  const [assignFor, setAssignFor] = useState<DashboardRow | null>(null);
  const [assignTeam, setAssignTeam] = useState<Team | null>(null);
  const [assignMembers, setAssignMembers] = useState<TeamMembers | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [filters, setFilters] = useState<FilterState>(BLANK_FILTERS);
  // Default to latest-first (newest enquiry on top). Score sort stays a toggle.
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Avika (2026-06-10) — WhatsApp line filter + responses drawer.
  const [lineFilter, setLineFilter] = useState<LineFilter>('all');
  const [responsesOpenFor, setResponsesOpenFor] = useState<DashboardRow | null>(null);
  const [exporting, setExporting] = useState(false);

  // Load teams once
  useEffect(() => {
    let cancelled = false;
    safeGet<Team[]>('/leads/triage-teams')
      .then((r) => { if (!cancelled && r?.data) setTeams(r.data); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  // Pagination — page-size dropdown (20/40/60/80/100) + offset paging.
  const [pageSize, setPageSize] = useState(20);
  const [offset, setOffset] = useState(0);
  // Reset to the first page whenever the query shape changes (tab/search/sort/
  // filters/page-size) so you never land on an out-of-range page.
  useEffect(() => { setOffset(0); }, [tab, search, sortKey, filters, lineFilter, pageSize]);

  // Load dashboard data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const q = buildTriageFilterParams({ tab, search, sortKey, filters, lineFilter });
    q.set('limit', String(pageSize));
    q.set('offset', String(offset));
    // Fetch the FULL envelope directly. safeGet() unwraps to res.data.data and
    // DROPS `total` → the pager read total=0 → "0 of 0", Next disabled, and
    // leads past the first page were unreachable. Read total from the envelope.
    fetch(`/api/v1/leads/triage-dashboard?${q.toString()}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((j) => {
        if (cancelled) return;
        setRows((j?.data as DashboardRow[]) ?? []);
        setTotal(typeof j?.total === 'number' ? j.total : 0);
      })
      .catch((e) => { if (!cancelled) { setRows([]); setTotal(0); setError(String(e?.message ?? e)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, search, sortKey, filters, lineFilter, pageSize, offset]);

  // Step 1: team chosen from the row dropdown → open the Assign modal + load members.
  async function openAssign(row: DashboardRow, team: Team) {
    setAssignFor(row);
    setAssignTeam(team);
    setAssignMembers(null);
    try {
      const r = await safeGet<TeamMembers>(`/leads/triage-teams/${team.id}/members`);
      if (r?.data) setAssignMembers(r.data);
    } catch { /* modal still works as manager+coordinator fallback */ }
  }

  // Step 2: submit — assignToUserId set → executive; omitted → manager+coordinator.
  async function submitAssign(assignToUserId?: string) {
    if (!assignFor || !assignTeam) return;
    const row = assignFor, team = assignTeam;
    setAssignBusy(true);
    const prev = rows;
    // Unassigned tab: optimistically drop the row (it leaves Unassigned once owned).
    // Assigned tab: this is a REASSIGN — keep the row; update it in place on success.
    if (tab === 'unassigned') setRows(rows.filter((x) => x.id !== row.id));
    try {
      const res = await fetch(`/api/v1/leads/${row.id}/assign-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignToUserId ? { teamId: team.id, assignToUserId } : { teamId: team.id }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      const who = j?.data?.mode === 'executive'
        ? j.data.assignedToName
        : `${j?.data?.assignedToName ?? 'Manager'}${j?.data?.coordinatorName ? ' + ' + j.data.coordinatorName : ''}`;
      // Reassign on the Assigned tab → reflect the new owner in place (don't drop the row).
      if (tab !== 'unassigned') {
        const d = j?.data ?? {};
        setRows((rs) => rs.map((x) => x.id === row.id
          ? { ...x, assignedToName: d.assignedToName ?? null, coordinatorName: d.coordinatorName ?? null, teamName: d.teamName ?? x.teamName }
          : x));
      }
      const toastId = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id: toastId, text: `Assigned to ${who} (${team.name})` }]);
      setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 5000);
      setAssignFor(null); setAssignTeam(null); setAssignMembers(null);
    } catch (e) {
      if (tab === 'unassigned') setRows(prev);
      setError(`Failed to assign: ${(e as Error).message}`);
    } finally {
      setAssignBusy(false);
    }
  }

  // Manual verify submit handler
  async function submitVerify() {
    if (!verifyOpenFor) return;
    setVerifySubmitting(true);
    try {
      const payload: Record<string, string> = {};
      if (verifyForm.productCategory.trim()) payload.productCategory = verifyForm.productCategory.trim();
      if (verifyForm.applicationType)        payload.applicationType = verifyForm.applicationType;
      if (verifyForm.businessSegment)        payload.businessSegment = verifyForm.businessSegment;
      if (verifyForm.location.trim())        payload.location = verifyForm.location.trim();
      if (verifyForm.requirement.trim())     payload.requirement = verifyForm.requirement.trim();
      if (verifyForm.notes.trim())           payload.notes = verifyForm.notes.trim();
      const res = await fetch(`/api/v1/leads/${verifyOpenFor.id}/verify-manually`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const verifiedByName = json?.data?.verifiedByName ?? null;
      // Update the row inline
      setRows((prev) => prev.map((row) => row.id === verifyOpenFor.id
        ? {
            ...row,
            ...(payload.location          ? { location: payload.location } : {}),
            ...(payload.requirement       ? { requirement: payload.requirement } : {}),
            ...(payload.applicationType   ? { applicationType: payload.applicationType } : {}),
            ...(payload.businessSegment   ? { businessSegment: payload.businessSegment } : {}),
            verifiedStatus: 'verified',
            verifiedBy: 'manual',
            verifiedByName,
          }
        : row));
      const toastId = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id: toastId, text: `Verified ${verifyOpenFor.name ?? verifyOpenFor.phone ?? 'lead'}` }]);
      setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 4000);
      setVerifyOpenFor(null);
      setVerifyForm(BLANK_VERIFY_FORM);
    } catch (e) {
      setError(`Verify failed: ${(e as Error).message}`);
    } finally {
      setVerifySubmitting(false);
    }
  }

  // Download an XLSX of EVERY lead matching the active filters (not just the
  // visible page). Reuses the same filter params as the table query.
  async function handleExport() {
    if (exporting) return;
    setExporting(true);
    try {
      const q = buildTriageFilterParams({ tab, search, sortKey, filters, lineFilter });
      const res = await fetch(`/api/v1/leads/triage-dashboard/export?${q.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const exportedRows = Number(res.headers.get('X-Aura-Export-Rows')) || 0;
      const capped = res.headers.get('X-Aura-Export-Capped') === '1';
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const fname = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? 'leads-triage-export.xlsx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const msg = capped
        ? `Exported ${exportedRows} leads (max reached — refine filters to export the rest)`
        : `Exported ${exportedRows} lead${exportedRows === 1 ? '' : 's'} to ${fname}`;
      const toastId = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id: toastId, text: msg }]);
      setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 4000);
    } catch (e) {
      setError(`Export failed: ${(e as Error).message}`);
    } finally {
      setExporting(false);
    }
  }

  const tabCounts = useMemo(() => ({ [tab]: total }), [tab, total]);

  return (
    <div className="p-4 md:p-6 max-w-screen-2xl mx-auto">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Lead Triage Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          Assign incoming leads to the right team. {total > 0 && (
            <span className="font-medium text-gray-700">{total} on this tab.</span>
          )}
        </p>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
            {tab === t.key && total > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                {total}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search + Filter bar */}
      <div className="mb-3 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-72 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Date preset */}
        <select
          value={
            filters.dateFrom === '' && filters.dateTo === '' ? 'all' :
            (() => {
              for (const p of ['today','7d','30d','mtd']) {
                const [f,t] = presetRange(p);
                if (filters.dateFrom === f && filters.dateTo === t) return p;
              }
              return 'custom';
            })()
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'all')         setFilters({ ...filters, dateFrom: '', dateTo: '' });
            else if (v === 'custom') { /* keep current */ }
            else {
              const [from, to] = presetRange(v);
              setFilters({ ...filters, dateFrom: from, dateTo: to });
            }
          }}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
          title="Date range"
        >
          <option value="all">All time</option>
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="mtd">Month to date</option>
          <option value="custom">Custom…</option>
        </select>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5"
          title="From date"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5"
          title="To date"
        />

        {/* Source chips */}
        <div className="flex items-center gap-1">
          {SOURCE_OPTIONS.map((s) => {
            const active = filters.sources.includes(s);
            return (
              <button
                key={s}
                onClick={() => setFilters({
                  ...filters,
                  sources: active ? filters.sources.filter((x) => x !== s) : [...filters.sources, s],
                })}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  active
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
              >{s}</button>
            );
          })}
        </div>

        {/* Verified status */}
        <select
          value={filters.verified}
          onChange={(e) => setFilters({ ...filters, verified: e.target.value as VerifiedFilter })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
          title="Verified status"
        >
          <option value="any">Verified: any</option>
          <option value="verified">Verified only</option>
          <option value="unverified">Unverified only</option>
        </select>

        {/* Avika — lead tier filter (Hot / Warm / Cold / Blank) */}
        <select
          value={filters.tier}
          onChange={(e) => setFilters({ ...filters, tier: e.target.value as TierFilter })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
          title="Lead tier"
        >
          <option value="any">Tier: any</option>
          <option value="HOT">Hot</option>
          <option value="WARM">Warm</option>
          <option value="COLD">Cold</option>
          <option value="BLANK">Blank (—)</option>
        </select>

        {/* Avika — WhatsApp line filter */}
        <div className="flex items-center gap-1" title="WhatsApp number / line">
          <span className="text-[11px] text-gray-500">Line:</span>
          {(['all', 'line1', 'line2'] as LineFilter[]).map((l) => (
            <button
              key={l}
              onClick={() => setLineFilter(l)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                lineFilter === l
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            >
              {l === 'all' ? 'All' : l === 'line1' ? 'Line 1' : 'Line 2'}
            </button>
          ))}
        </div>

        {/* Avika — Score sort toggle */}
        <button
          onClick={() => setSortKey(sortKey === 'score_desc' ? 'score_asc' : 'score_desc')}
          className={`text-xs px-2 py-1.5 rounded border transition-colors ${
            sortKey === 'score_desc' || sortKey === 'score_asc'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
          }`}
          title="Sort by lead score"
        >
          Score {sortKey === 'score_desc' ? '↓' : sortKey === 'score_asc' ? '↑' : '↕'}
        </button>

        {/* More filters toggle */}
        <button
          onClick={() => setFiltersOpen((o) => !o)}
          className="text-xs px-2 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
        >
          {filtersOpen ? 'Less ▴' : 'More ▾'}
        </button>

        {/* Clear all (only when filter active) */}
        {(filters.dateFrom || filters.dateTo || filters.sources.length || filters.applicationTypes.length ||
          filters.businessSegments.length || filters.location || filters.verified !== 'any' ||
          filters.verifiedSource !== 'any' || filters.tier !== 'any' || lineFilter !== 'all') && (
          <button
            onClick={() => { setFilters(BLANK_FILTERS); setLineFilter('all'); }}
            className="text-xs text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline"
          >Clear all</button>
        )}

        {/* Export filtered leads to XLSX */}
        <button
          onClick={handleExport}
          disabled={exporting || loading}
          className="ml-auto text-xs px-3 py-1.5 rounded border border-green-600 bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          title="Download every lead matching the current filters as an Excel file"
        >
          {exporting ? 'Exporting…' : (
            <>
              <span aria-hidden>⬇</span> Download XLSX
            </>
          )}
        </button>
      </div>

      {/* Expanded filter row */}
      {filtersOpen && (
        <div className="mb-3 p-3 border border-gray-200 rounded-md bg-gray-50 flex flex-wrap gap-3 items-center">
          {/* Application type chips */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 mr-1">Application:</span>
            {APP_TYPE_OPTIONS.map((s) => {
              const active = filters.applicationTypes.includes(s);
              return (
                <button
                  key={s}
                  onClick={() => setFilters({
                    ...filters,
                    applicationTypes: active ? filters.applicationTypes.filter((x) => x !== s) : [...filters.applicationTypes, s],
                  })}
                  className={`text-xs px-2 py-1 rounded border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                >{s}</button>
              );
            })}
          </div>
          {/* B2B/B2C chips */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 mr-1">Segment:</span>
            {SEGMENT_OPTIONS.map((s) => {
              const active = filters.businessSegments.includes(s);
              return (
                <button
                  key={s}
                  onClick={() => setFilters({
                    ...filters,
                    businessSegments: active ? filters.businessSegments.filter((x) => x !== s) : [...filters.businessSegments, s],
                  })}
                  className={`text-xs px-2 py-1 rounded border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                >{s}</button>
              );
            })}
          </div>
          {/* Location */}
          <label className="text-xs flex items-center gap-1">
            <span className="text-gray-500">Location:</span>
            <input
              type="text"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              placeholder="e.g. Panipat"
              className="text-xs border border-gray-300 rounded px-2 py-1 w-32"
            />
          </label>
          {/* Verified source */}
          <select
            value={filters.verifiedSource}
            onChange={(e) => setFilters({ ...filters, verifiedSource: e.target.value as VerifiedSourceFilter })}
            className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            title="Verified by"
          >
            <option value="any">Verified by: any</option>
            <option value="chatkart">ChatKart (Ananya)</option>
            <option value="manual">Manual (rep call)</option>
          </select>
        </div>
      )}

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Table.
          Windows-mouse horizontal scrolling (2026-06-11): mouse wheels only
          scroll vertically, so a wide table was effectively unscrollable
          without a trackpad. Three affordances:
            1. drag-to-pan — press-and-drag any empty spot of the table
            2. always-visible styled horizontal scrollbar (12px hit target)
            3. vertical wheel translates to horizontal scroll while hovering
               the table whenever the PAGE has nothing left to scroll for that
               wheel direction (Shift+wheel stays native)
          Drag suppresses the click that would otherwise fire on row buttons. */}
      <style>{`
        .triage-hscroll { scrollbar-width: auto; scrollbar-color: #94a3b8 #f1f5f9; }
        .triage-hscroll::-webkit-scrollbar { height: 12px; }
        .triage-hscroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 6px; border: 2px solid #f1f5f9; }
        .triage-hscroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
        .triage-hscroll::-webkit-scrollbar-track { background: #f1f5f9; }
      `}</style>
      <div
        ref={hscrollRef}
        className="bg-white border border-gray-200 rounded-lg overflow-x-auto triage-hscroll"
        style={{ cursor: 'grab' }}
        onPointerDown={(e) => {
          // Left button only; never hijack interactive elements.
          if (e.button !== 0) return;
          const target = e.target as HTMLElement;
          if (target.closest('button, select, input, a, textarea')) return;
          const el = hscrollRef.current;
          if (!el) return;
          dragRef.current = { startX: e.clientX, startLeft: el.scrollLeft, moved: false };
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          const el = hscrollRef.current;
          if (!d || !el) return;
          const dx = e.clientX - d.startX;
          if (!d.moved && Math.abs(dx) < 6) return; // dead zone — keep clicks working
          if (!d.moved) {
            d.moved = true;
            el.setPointerCapture?.(e.pointerId);
            el.style.cursor = 'grabbing';
          }
          el.scrollLeft = d.startLeft - dx;
        }}
        onPointerUp={(e) => {
          const el = hscrollRef.current;
          if (el) {
            el.releasePointerCapture?.(e.pointerId);
            el.style.cursor = 'grab';
          }
          // Keep `moved` until the click event fires (click follows pointerup).
          const d = dragRef.current;
          dragRef.current = null;
          if (d?.moved) {
            suppressClickRef.current = true;
            setTimeout(() => { suppressClickRef.current = false; }, 0);
          }
        }}
        onPointerCancel={() => {
          const el = hscrollRef.current;
          if (el) el.style.cursor = 'grab';
          dragRef.current = null;
        }}
        onClickCapture={(e) => {
          if (suppressClickRef.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onWheel={(e) => {
          const el = hscrollRef.current;
          if (!el) return;
          // Shift+wheel already scrolls horizontally natively. Translate a
          // plain vertical wheel into horizontal pan only when the table can
          // still move that way — never trap the user's page scroll.
          if (e.shiftKey || e.deltaX !== 0 || e.deltaY === 0) return;
          const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
          const canLeft = el.scrollLeft > 0;
          const doc = document.documentElement;
          const pageCanScroll = e.deltaY > 0
            ? window.scrollY + window.innerHeight < doc.scrollHeight - 1
            : window.scrollY > 0;
          if (pageCanScroll) return; // page motion wins while it has room
          // No preventDefault — React wheel listeners are passive, and the
          // page has no scroll room left here anyway (checked above).
          if ((e.deltaY > 0 && canRight) || (e.deltaY < 0 && canLeft)) {
            el.scrollLeft += e.deltaY;
          }
        }}
      >
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Sl. No.</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'newest' ? 'oldest' : 'newest')}
              >
                Enquiry Date {sortKey === 'newest' ? '↓' : sortKey === 'oldest' ? '↑' : '↕'}
              </th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'source_asc' ? 'source_desc' : 'source_asc')}
              >
                Enquiry Source {sortKey === 'source_asc' ? '↑' : sortKey === 'source_desc' ? '↓' : '↕'}
              </th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'name_asc' ? 'name_desc' : 'name_asc')}
              >
                Customer Name {sortKey === 'name_asc' ? '↑' : sortKey === 'name_desc' ? '↓' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Contact Number</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'location_asc' ? 'location_desc' : 'location_asc')}
              >
                Location / City {sortKey === 'location_asc' ? '↑' : sortKey === 'location_desc' ? '↓' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Requirement</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Application Type</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">B2B / B2C</th>
              {/* ── Avika (2026-06-10) columns ─────────────────────────────── */}
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Line</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Capacity</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Timeline</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Price shared</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'score_desc' ? 'score_asc' : 'score_desc')}
              >
                Lead Score {sortKey === 'score_desc' ? '↓' : sortKey === 'score_asc' ? '↑' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Tier</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Responses</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Copy</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Verified</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">
                {tab === 'unassigned' ? 'Assign To Team' : tab === 'assigned' ? 'Team' : 'Created By'}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100 animate-pulse">
                    {Array.from({ length: 18 }).map((__, j) => (
                      <td key={j} className="px-3 py-3">
                        <div className="h-3 bg-gray-100 rounded w-20" />
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={19} className="px-3 py-12 text-center text-sm text-gray-500">
                  {tab === 'unassigned' && (
                    <>
                      All caught up. Ananya will surface new leads here within 5 min.<br />
                      <span className="text-xs">Last sync: just now.</span>
                    </>
                  )}
                  {tab === 'assigned' && 'No leads have been assigned yet.'}
                  {tab === 'self_made' &&
                    'Self-made leads appear when reps add them from the AURA Sales app. Coming soon.'}
                </td>
              </tr>
            )}

            {!loading && rows.map((r, idx) => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                <td className="px-3 py-2 text-gray-700">{formatDate(r.createdAt)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        r.verifiedStatus === 'verified' ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      title={r.verifiedStatus === 'verified' ? 'Verified by Ananya' : 'Unverified'}
                    />
                    <Cell value={r.source} />
                  </div>
                </td>
                <td className="px-3 py-2"><Cell value={r.name} /></td>
                <td className="px-3 py-2 font-mono text-xs text-gray-800">{r.phone || <Cell value={null} />}</td>
                <td className="px-3 py-2"><Cell value={[r.location, r.state].filter(Boolean).join(', ') || null} /></td>
                <td className="px-3 py-2"><Cell value={r.requirement} /></td>
                <td className="px-3 py-2"><Cell value={r.applicationType} /></td>
                <td className="px-3 py-2"><Cell value={r.businessSegment} /></td>
                {/* ── Avika (2026-06-10) cells ─────────────────────────────── */}
                <td className="px-3 py-2">
                  {r.waLineLabel ? (
                    <span className="inline-flex items-center text-xs font-medium text-gray-700 bg-gray-100 rounded px-1.5 py-0.5">
                      {r.waLineLabel === 'line1' ? 'Line 1' : r.waLineLabel === 'line2' ? 'Line 2' : r.waLineLabel}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2"><Cell value={formatCapacitySummary(r.capacity, r.quantity) || null} /></td>
                <td className="px-3 py-2"><Cell value={r.timeline} /></td>
                <td className="px-3 py-2">
                  {r.priceShared ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      {r.priceRange || 'Yes'}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">No</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.score == null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <span className="font-semibold text-gray-900">{r.score}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <TierChip tier={r.tier} isB2bPriority={r.isB2bPriority} isOutOfBudget={r.isOutOfBudget} />
                </td>
                <td className="px-3 py-2">
                  {r.verifiedBy === 'avika' || r.score != null ? (
                    <button
                      onClick={() => setResponsesOpenFor(r)}
                      className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 border border-blue-300 hover:border-blue-500 rounded px-2 py-0.5 transition-colors"
                      title="View the full WhatsApp Q&A + score breakdown"
                    >
                      Responses
                    </button>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2"><CopyButton text={buildLeadSummary(r)} /></td>
                <td className="px-3 py-2">
                  {r.verifiedStatus === 'verified' ? (
                    r.verifiedBy === 'avika' ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded"
                        title="Qualified by Avika (ChatKart line 2)"
                      >
                        <span className="w-1.5 h-1.5 bg-violet-500 rounded-full" />
                        Avika
                      </span>
                    ) : r.verifiedBy === 'manual' ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded"
                        title={`Manually verified${r.verifiedByName ? ' by ' + r.verifiedByName : ''}`}
                      >
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                        Manual{r.verifiedByName ? ` · ${r.verifiedByName}` : ''}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded"
                        title="Verified by Ananya (ChatKart)"
                      >
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        ChatKart
                      </span>
                    )
                  ) : (
                    <button
                      onClick={() => { setVerifyOpenFor(r); setVerifyForm(BLANK_VERIFY_FORM); }}
                      className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 border border-blue-300 hover:border-blue-500 rounded px-2 py-0.5 transition-colors"
                      title="Call the customer and record their answers"
                    >
                      Verify
                    </button>
                  )}
                </td>
                <td className="px-3 py-2">
                  {tab === 'unassigned' ? (
                    <select
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      defaultValue=""
                      onChange={(e) => {
                        const teamId = e.target.value;
                        if (!teamId) return;
                        const team = teams.find((t) => t.id === teamId);
                        if (team) openAssign(r, team);
                        e.target.value = '';
                      }}
                    >
                      <option value="">Assign to ▾</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  ) : tab === 'assigned' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-800 whitespace-nowrap">
                        {[r.assignedToName, r.coordinatorName].filter(Boolean).join(' + ') || r.teamName || '—'}
                      </span>
                      <select
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        defaultValue=""
                        onChange={(e) => {
                          const teamId = e.target.value;
                          if (!teamId) return;
                          const team = teams.find((t) => t.id === teamId);
                          if (team) openAssign(r, team);
                          e.target.value = '';
                        }}
                      >
                        <option value="">Reassign ▾</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <Cell value={r.assignedToName} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination — page size (20/40/60/80/100) + offset paging */}
      <div className="flex items-center justify-between gap-3 px-1 py-3 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <span>Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[20, 40, 60, 80, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span className="tabular-nums">
            {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + pageSize, total)}`} of {total}
          </span>
          <button
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
            className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-40 hover:border-gray-400"
          >Prev</button>
          <button
            disabled={offset + pageSize >= total || loading}
            onClick={() => setOffset((o) => o + pageSize)}
            className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-40 hover:border-gray-400"
          >Next</button>
        </div>
      </div>

      {/* Avika Responses Drawer */}
      <LeadResponsesDrawer
        leadId={responsesOpenFor?.id ?? null}
        onClose={() => setResponsesOpenFor(null)}
      />

      {/* Manual Verify Modal */}
      {verifyOpenFor && (
        <div
          className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
          onClick={() => !verifySubmitting && setVerifyOpenFor(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Verify lead manually</h2>
              <button
                onClick={() => setVerifyOpenFor(null)}
                disabled={verifySubmitting}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none disabled:opacity-50"
                aria-label="Close"
              >×</button>
            </div>
            <div className="mb-3 text-sm">
              <div className="font-medium text-gray-900">{verifyOpenFor.name || '(no name)'}</div>
              <div className="font-mono text-xs text-gray-600">{verifyOpenFor.phone || '(no phone)'}</div>
              <div className="text-xs text-gray-500 mt-1">
                Call the customer, ask their requirement, and record the answers below.
              </div>
            </div>
            <div className="space-y-3">
              <label className="block text-sm">
                <span className="text-gray-700">Product / category</span>
                <input
                  type="text"
                  value={verifyForm.productCategory}
                  onChange={(e) => setVerifyForm((f) => ({ ...f, productCategory: e.target.value }))}
                  placeholder="e.g. SS Water Tank"
                  className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-700">Application type</span>
                  <select
                    value={verifyForm.applicationType}
                    onChange={(e) => setVerifyForm((f) => ({ ...f, applicationType: e.target.value as VerifyForm['applicationType'] }))}
                    className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded bg-white"
                  >
                    <option value="">—</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="B2B">B2B</option>
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-700">B2B / B2C</span>
                  <select
                    value={verifyForm.businessSegment}
                    onChange={(e) => setVerifyForm((f) => ({ ...f, businessSegment: e.target.value as VerifyForm['businessSegment'] }))}
                    className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded bg-white"
                  >
                    <option value="">—</option>
                    <option value="B2B">B2B</option>
                    <option value="B2C">B2C</option>
                  </select>
                </label>
              </div>
              <label className="block text-sm">
                <span className="text-gray-700">Location / city</span>
                <input
                  type="text"
                  value={verifyForm.location}
                  onChange={(e) => setVerifyForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Panipat"
                  className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">Requirement</span>
                <textarea
                  rows={2}
                  value={verifyForm.requirement}
                  onChange={(e) => setVerifyForm((f) => ({ ...f, requirement: e.target.value }))}
                  placeholder="What did the customer ask for?"
                  className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">Notes (internal)</span>
                <textarea
                  rows={2}
                  value={verifyForm.notes}
                  onChange={(e) => setVerifyForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Any context the team should know"
                  className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setVerifyOpenFor(null)}
                disabled={verifySubmitting}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={submitVerify}
                disabled={verifySubmitting}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >{verifySubmitting ? 'Verifying…' : 'Mark verified'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-50">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="bg-gray-900 text-white text-sm rounded-lg shadow-lg px-4 py-3 flex items-center gap-3"
          >
            <span>{t.text}</span>
            {t.undo && (
              <button
                onClick={t.undo}
                className="text-blue-300 hover:text-blue-100 font-medium underline-offset-2 hover:underline"
              >
                Undo
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Assign Modal — team → executive | manager+coordinator */}
      {assignFor && assignTeam && (
        <div
          className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
          onClick={() => !assignBusy && (setAssignFor(null), setAssignTeam(null))}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold text-gray-900">Assign lead</h2>
              <button onClick={() => { setAssignFor(null); setAssignTeam(null); }} disabled={assignBusy}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none disabled:opacity-50" aria-label="Close">×</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Team: <span className="font-medium text-gray-800">{assignTeam.name}</span></p>

            <div className="mb-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Assign to an executive</div>
              {assignMembers === null ? (
                <div className="text-sm text-gray-400">Loading team…</div>
              ) : assignMembers.executives.length === 0 ? (
                <div className="text-sm text-gray-400">No executives in this team.</div>
              ) : (
                <div className="flex flex-col gap-1">
                  {assignMembers.executives.map((ex) => (
                    <button key={ex.id} disabled={assignBusy} onClick={() => submitAssign(ex.id)}
                      className="text-left px-3 py-2 rounded border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-sm disabled:opacity-50">
                      {ex.name} <span className="text-gray-400">· Executive</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 pt-3">
              <button disabled={assignBusy} onClick={() => submitAssign()}
                className="w-full px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                Assign to Manager + Coordinator
              </button>
              <p className="text-xs text-gray-400 mt-1 text-center">
                {assignMembers?.manager ? assignMembers.manager.name : 'Manager'}
                {assignMembers?.coordinator ? ` + ${assignMembers.coordinator.name}` : ''}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadTriageDashboard;
